import React from 'react';
import { SEO } from '../components/layout/SEO';

const PageWrapper = ({ title, description, url, children }) => (
  <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
    <SEO 
      title={`${title} | FreeToolSuitePro`} 
      description={description} 
      url={`https://freetoolsuitepro.com${url}`} 
    />
    <h1 className="text-4xl font-extrabold text-slate-900 dark:text-white mb-8 tracking-tight">{title}</h1>
    <div className="prose prose-lg prose-slate dark:prose-invert max-w-none">
      {children}
    </div>
  </div>
);

export const About = () => (
  <PageWrapper 
    title="About FreeToolSuitePro" 
    description="Learn more about FreeToolSuitePro and our mission to provide free, private, client-side tools."
    url="/about"
  >
    <p>Welcome to FreeToolSuitePro, your ultimate destination for free, fast, and completely private online utilities.</p>
    <h2>Our Mission</h2>
    <p>We built this platform to solve a simple problem: the internet is full of "free" tools that actually require signups, show intrusive ads, or worse—secretly upload your sensitive files to their servers.</p>
    <p>At FreeToolSuitePro, <strong>100% of our file processing happens entirely in your browser</strong>. Your files never leave your device, ensuring total privacy and security.</p>
    
    <h2>Why Choose Us?</h2>
    <ul>
      <li><strong>Zero Server Uploads:</strong> We leverage modern Web APIs to process everything client-side.</li>
      <li><strong>Lightning Fast:</strong> No waiting for uploads or server queues. Processing happens at the speed of your device.</li>
      <li><strong>Always Free:</strong> No premium tiers, no subscriptions, no hidden fees.</li>
    </ul>
  </PageWrapper>
);

export const PrivacyPolicy = () => (
  <PageWrapper 
    title="Privacy Policy"
    description="Read the Privacy Policy of FreeToolSuitePro. Understand how we protect your data with our 100% client-side architecture."
    url="/privacy-policy"
  >
    <p>Last updated: {new Date().toLocaleDateString()}</p>
    <h2>1. Data processing</h2>
    <p>The core principle of FreeToolSuitePro is client-side processing. When you use our file manipulation tools (like image compressors or PDF utilities), the files are processed entirely within your web browser. <strong>We do not upload, store, or transmit your files to any external servers.</strong></p>
    
    <h2>2. Information we collect</h2>
    <p>We do not collect personal information unless you explicitly provide it (e.g., via a contact form). We may use standard web analytics to understand general traffic patterns, which involves anonymous telemetry like page views and browser types.</p>
    
    <h2>3. Cookies</h2>
    <p>We use minimal local storage to save your preferences, such as your dark mode toggle. We do not use tracking cookies for advertising purposes.</p>
  </PageWrapper>
);

export const Terms = () => (
  <PageWrapper 
    title="Terms of Service"
    description="Terms of Service and usage conditions for FreeToolSuitePro's online utilities."
    url="/terms"
  >
    <p>Last updated: {new Date().toLocaleDateString()}</p>
    <h2>1. Acceptance of Terms</h2>
    <p>By accessing and using FreeToolSuitePro, you accept and agree to be bound by the terms and provision of this agreement.</p>
    
    <h2>2. Use License</h2>
    <p>Permission is granted to temporarily use the tools on FreeToolSuitePro's website for personal, non-commercial transitory viewing only.</p>
    
    <h2>3. Disclaimer</h2>
    <p>The materials on FreeToolSuitePro's website are provided on an 'as is' basis. We make no warranties, expressed or implied, and hereby disclaim and negate all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.</p>
  </PageWrapper>
);

export const Contact = () => (
  <PageWrapper 
    title="Contact Us"
    description="Get in touch with the team behind FreeToolSuitePro for support, feedback, or business inquiries."
    url="/contact"
  >
    <p>Have a question, suggestion, or found a bug? We'd love to hear from you.</p>
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-8 mt-8 shadow-sm">
      <form 
        onSubmit={(e) => {
          e.preventDefault();
          const formData = new FormData(e.target);
          const subject = formData.get('subject');
          const body = formData.get('message');
          window.location.href = `mailto:support@freetoolsuitepro.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        }}
        className="space-y-6"
      >
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Subject</label>
          <input 
            type="text" 
            id="subject"
            name="subject"
            required
            className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-shadow"
            placeholder="What is this regarding?"
          />
        </div>
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Message</label>
          <textarea 
            id="message"
            name="message"
            rows="5"
            required
            className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-shadow resize-none"
            placeholder="Type your message here..."
          ></textarea>
        </div>
        <button 
          type="submit"
          className="w-full py-3 px-6 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors shadow-sm"
        >
          Open Email Client
        </button>
        <p className="text-xs text-center text-slate-500 mt-4">
          This will open your default email application to send the message. We don't store your email address.
        </p>
      </form>
    </div>
  </PageWrapper>
);